import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Plus, Loader2, AlertTriangle, Pencil, Trash2, Layers, Settings, ChevronRight } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import { cn } from '@/app/components/ui/utils';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Switch } from '@/app/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { toast } from '@/app/components/ui/simple-toaster';
import { useCFLocation, useUpdateCFLocation } from '@/hooks/use-cf-locations';
import { useZones, useCreateZone, useUpdateZone, useDeleteZone } from '@/hooks/use-zones';
import { useSpots, useCreateSpot, useUpdateSpot, useDeleteSpot } from '@/hooks/use-spots';
import { useLocationTemplates, useApplyTemplate } from '@/hooks/use-location-templates';
import { useIndustry } from '@/hooks/use-industry';
import { useInstalledGearOverrun } from '@/hooks/use-assets';
import { usePlatformModules } from '@/hooks/use-modules';
import { ZonePicker } from '@/components/shared/zone-picker';
import { WorkHistoryView } from '@/app/pages/work/components/work-history-view';
import { handleApiError } from '@/lib/api-client';
import type { ZoneType, SpotType, Zone, Spot, Industry, LocationTemplate, InstalledAssetOverrunItem, CFLocation } from '@/lib/types';

const ZONE_TYPE_DEFAULTS: Record<Industry, ZoneType> = {
  field_ops: 'area',
  facility_ops: 'department',
  training_ops: 'training_area',
  consultant: 'department',
  hybrid: 'area',
};

const SPOT_TYPE_DEFAULTS: Record<Industry, SpotType> = {
  field_ops: 'toolbox',
  facility_ops: 'locker',
  training_ops: 'equipment_store',
  consultant: 'other',
  hybrid: 'toolbox',
};

function pluralize(word: string): string {
  if (word.endsWith('x') || word.endsWith('s') || word.endsWith('sh') || word.endsWith('ch')) return word + 'es';
  return word + 's';
}

const ZONE_TYPES: { value: ZoneType; label: string }[] = [
  { value: 'area', label: 'Area' },
  { value: 'floor', label: 'Floor' },
  { value: 'department', label: 'Department' },
  { value: 'training_area', label: 'Training Area' },
  { value: 'section', label: 'Section' },
  { value: 'other', label: 'Other' },
];

const SPOT_TYPES: { value: SpotType; label: string }[] = [
  { value: 'toolbox', label: 'Toolbox' },
  { value: 'anchor_point', label: 'Anchor Point' },
  { value: 'locker', label: 'Locker' },
  { value: 'equipment_store', label: 'Equipment Store' },
  { value: 'depot_shelf', label: 'Depot Shelf' },
  { value: 'cage', label: 'Cage' },
  { value: 'vehicle_compartment', label: 'Vehicle Compartment' },
  { value: 'desk', label: 'Desk' },
  { value: 'other', label: 'Other' },
];

const TEMPLATE_PARAM_LABELS: Record<string, string> = {
  floors: 'Number of floors',
  units: 'Number of units',
};

export function LocationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const locationId = Number(id);
  const { labels, industry } = useIndustry();

  const { data: location, isLoading } = useCFLocation(locationId);
  const { data: zonesData, isLoading: zonesLoading } = useZones({ location: locationId, page_size: 1000 });
  const { data: spotsData, isLoading: spotsLoading } = useSpots({ location: locationId });
  const { data: overrunData, isLoading: overrunLoading } = useInstalledGearOverrun(locationId);
  const { isModuleEnabled } = usePlatformModules();
  const workEnabled = isModuleEnabled('work');

  const zones = zonesData?.results ?? [];
  const spots = spotsData?.results ?? [];
  const overrunCount = overrunData?.count ?? 0;

  const [addZoneOpen, setAddZoneOpen] = useState(false);
  const [addZoneParentId, setAddZoneParentId] = useState<number | null>(null);
  const [addSpotOpen, setAddSpotOpen] = useState(false);
  const [editZone, setEditZone] = useState<Zone | null>(null);
  const [editSpot, setEditSpot] = useState<Spot | null>(null);
  const [applyTemplateOpen, setApplyTemplateOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!location) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900">{labels.location} not found</h3>
          </CardContent>
        </Card>
      </div>
    );
  }

  const addressParts = [location.address, location.city, location.state_province, location.country]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to="/locations">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <MapPin className="h-7 w-7" />
            {location.name}
          </h1>
          {addressParts && <p className="text-gray-600 mt-1">{addressParts}</p>}
        </div>
        <Button variant="outline" onClick={() => setApplyTemplateOpen(true)}>
          <Layers className="h-4 w-4 mr-2" />
          Apply a template
        </Button>
        <Badge
          variant="outline"
          className={
            location.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }
        >
          {location.is_active ? 'Active' : 'Inactive'}
        </Badge>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="zones">
        <TabsList>
          <TabsTrigger value="zones">
            {pluralize(labels.zone)} ({zonesData?.count ?? 0})
          </TabsTrigger>
          <TabsTrigger value="spots">
            {pluralize(labels.spot)} ({spotsData?.count ?? 0})
          </TabsTrigger>
          <TabsTrigger value="gear-due" className="relative">
            Gear Due
            {overrunCount > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-red-100 text-red-700 text-[10px] font-semibold px-1.5 min-w-[18px] h-[18px]">
                {overrunCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="h-4 w-4 mr-1.5" />
            Settings
          </TabsTrigger>
          {workEnabled && (
            <TabsTrigger value="work-history">
              Work history
            </TabsTrigger>
          )}
        </TabsList>

        {/* Zones Tab */}
        <TabsContent value="zones" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setAddZoneParentId(null); setAddZoneOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Add {labels.zone}
            </Button>
          </div>

          {zonesLoading && (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}

          {!zonesLoading && zones.length === 0 && (
            <Card>
              <CardContent className="pt-6 text-center py-8">
                <p className="text-gray-500">No {pluralize(labels.zone).toLowerCase()} yet.</p>
              </CardContent>
            </Card>
          )}

          {!zonesLoading && zones.length > 0 && (
            <>
              <ZoneTreeTable
                zones={zones}
                labels={labels}
                onEdit={setEditZone}
                onAddChild={(zone) => {
                  setAddZoneParentId(zone.id);
                  setAddZoneOpen(true);
                }}
              />
              <ZoneTreeCards
                zones={zones}
                labels={labels}
                onEdit={setEditZone}
                onAddChild={(zone) => {
                  setAddZoneParentId(zone.id);
                  setAddZoneOpen(true);
                }}
              />
            </>
          )}
        </TabsContent>

        {/* Spots Tab */}
        <TabsContent value="spots" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setAddSpotOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add {labels.spot}
            </Button>
          </div>

          {spotsLoading && (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}

          {!spotsLoading && spots.length === 0 && (
            <Card>
              <CardContent className="pt-6 text-center py-8">
                <p className="text-gray-500">No {pluralize(labels.spot).toLowerCase()} yet.</p>
              </CardContent>
            </Card>
          )}

          {!spotsLoading && spots.length > 0 && (
            <>
              {/* Desktop table */}
              <div className="hidden lg:block border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>{labels.zone}</TableHead>
                      <TableHead>Active</TableHead>
                      <TableHead className="w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {spots.map((spot) => {
                      const typeLabel =
                        SPOT_TYPES.find((t) => t.value === spot.type)?.label ?? spot.type;
                      const zoneName = spot.zone
                        ? zones.find((z) => z.id === spot.zone)?.path ?? '—'
                        : '(Direct)';
                      return (
                        <TableRow key={spot.id}>
                          <TableCell className="font-medium">{spot.name}</TableCell>
                          <TableCell>{typeLabel}</TableCell>
                          <TableCell>{zoneName}</TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={
                                spot.is_active
                                  ? 'bg-green-50 text-green-700'
                                  : 'bg-red-50 text-red-700'
                              }
                            >
                              {spot.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setEditSpot(spot)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <DeleteSpotButton spot={spot} label={labels.spot} />
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile card list */}
              <div className="lg:hidden space-y-2">
                {spots.map((spot) => {
                  const typeLabel =
                    SPOT_TYPES.find((t) => t.value === spot.type)?.label ?? spot.type;
                  const zoneName = spot.zone
                    ? zones.find((z) => z.id === spot.zone)?.path ?? '—'
                    : '(Direct)';
                  return (
                    <Card key={spot.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900">{spot.name}</p>
                            <p className="text-xs text-muted-foreground">{typeLabel}</p>
                            <p className="text-xs text-muted-foreground">{labels.zone}: {zoneName}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setEditSpot(spot)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <DeleteSpotButton spot={spot} label={labels.spot} />
                            <Badge
                              variant="outline"
                              className={
                                spot.is_active
                                  ? 'bg-green-50 text-green-700 flex-shrink-0'
                                  : 'bg-red-50 text-red-700 flex-shrink-0'
                              }
                            >
                              {spot.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </TabsContent>

        {/* Settings Tab — per-location overrides */}
        <TabsContent value="settings" className="space-y-4">
          <LocationOverrideSettings location={location} />
        </TabsContent>

        {/* Installed Gear Due Tab */}
        <TabsContent value="gear-due" className="space-y-4">
          {overrunLoading && (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}

          {!overrunLoading && overrunCount === 0 && (
            <Card>
              <CardContent className="pt-6 text-center py-8">
                <p className="text-gray-500">No installed gear due for inspection.</p>
              </CardContent>
            </Card>
          )}

          {!overrunLoading && overrunCount > 0 && (
            <>
              <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>
                  {overrunCount} installed asset{overrunCount !== 1 ? 's' : ''} overdue for inspection
                </span>
              </div>

              {/* Desktop table */}
              <div className="hidden lg:block border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Asset</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>{labels.zone} / {labels.spot}</TableHead>
                      <TableHead>Inspection Due</TableHead>
                      <TableHead>Days Overdue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(overrunData?.results ?? []).map((item: InstalledAssetOverrunItem) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Link
                            to={`/assets/${item.id}`}
                            className="font-medium text-blue-600 hover:underline"
                          >
                            {item.display_name}
                          </Link>
                          <p className="text-xs text-muted-foreground font-mono">{item.serial_number}</p>
                        </TableCell>
                        <TableCell className="capitalize">{item.type}</TableCell>
                        <TableCell>
                          {item.zone_name && item.spot_name
                            ? `${item.zone_name} / ${item.spot_name}`
                            : item.zone_name ?? item.spot_name ?? '—'}
                        </TableCell>
                        <TableCell>{item.next_inspection_due ?? '—'}</TableCell>
                        <TableCell>
                          <Badge variant="destructive">{item.days_overdue ?? 0}d</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile card list */}
              <div className="lg:hidden space-y-2">
                {(overrunData?.results ?? []).map((item: InstalledAssetOverrunItem) => (
                  <Card key={item.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <Link
                            to={`/assets/${item.id}`}
                            className="font-medium text-blue-600 hover:underline"
                          >
                            {item.display_name}
                          </Link>
                          <p className="text-xs text-muted-foreground font-mono">{item.serial_number}</p>
                          <p className="text-xs text-muted-foreground capitalize">{item.type}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.zone_name && item.spot_name
                              ? `${item.zone_name} / ${item.spot_name}`
                              : item.zone_name ?? item.spot_name ?? '—'}
                          </p>
                        </div>
                        <Badge variant="destructive">{item.days_overdue ?? 0}d overdue</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {workEnabled && (
          <TabsContent value="work-history" className="space-y-4">
            <WorkHistoryView locationId={locationId} />
          </TabsContent>
        )}
      </Tabs>

      <AddZoneDialog
        open={addZoneOpen}
        onOpenChange={(open) => {
          if (!open) setAddZoneParentId(null);
          setAddZoneOpen(open);
        }}
        locationId={locationId}
        parentZoneId={addZoneParentId}
        industry={industry}
        labels={labels}
      />
      <AddSpotDialog
        open={addSpotOpen}
        onOpenChange={setAddSpotOpen}
        locationId={locationId}
        zones={zones}
        industry={industry}
        labels={labels}
      />
      <EditZoneDialog
        zone={editZone}
        onClose={() => setEditZone(null)}
        locationId={locationId}
        labels={labels}
      />
      <EditSpotDialog
        spot={editSpot}
        onClose={() => setEditSpot(null)}
        zones={zones}
        labels={labels}
      />
      <ApplyTemplateDialog
        open={applyTemplateOpen}
        onOpenChange={setApplyTemplateOpen}
        locationId={locationId}
      />
    </div>
  );
}

// ─── Location Override Settings ──────────────────────────────────────────────

function LocationOverrideSettings({ location }: { location: CFLocation }) {
  const updateLocation = useUpdateCFLocation();
  const effective = location.effective_settings;

  const handleTogglePersistence = async (checked: boolean) => {
    const newVal = checked ? 'persistent' : null;
    try {
      await updateLocation.mutateAsync({
        id: location.id,
        data: { persistence_override: newVal },
      });
      toast.success(
        newVal
          ? 'Location marked as persistent (overrides operating-model default).'
          : 'Persistence override removed — follows operating-model default.'
      );
    } catch (err) {
      const apiError = handleApiError(err);
      toast.error(apiError.message);
    }
  };

  const handleToggleTrackGear = async (checked: boolean) => {
    const newVal = checked ? true : null;
    try {
      await updateLocation.mutateAsync({
        id: location.id,
        data: { track_installed_gear_override: newVal },
      });
      toast.success(
        newVal
          ? 'Installed-gear tracking enabled (overrides operating-model default).'
          : 'Installed-gear tracking override removed — follows operating-model default.'
      );
    } catch (err) {
      const apiError = handleApiError(err);
      toast.error(apiError.message);
    }
  };

  const handleToggleGearLifecycle = async (checked: boolean) => {
    const newVal = checked ? 'inspect_in_place' : null;
    try {
      await updateLocation.mutateAsync({
        id: location.id,
        data: { gear_lifecycle_override: newVal },
      });
      toast.success(
        newVal
          ? 'Gear lifecycle set to inspect-in-place (overrides operating-model default).'
          : 'Gear lifecycle override removed — follows operating-model default.'
      );
    } catch (err) {
      const apiError = handleApiError(err);
      toast.error(apiError.message);
    }
  };

  const isPersistenceOverridden = location.persistence_override === 'persistent';
  const isTrackGearOverridden = location.track_installed_gear_override === true;
  const isGearLifecycleOverridden = location.gear_lifecycle_override === 'inspect_in_place';

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-amber-50 border-amber-200 px-4 py-3 text-sm text-amber-800">
        These overrides let you change how this specific location behaves, regardless of your
        organisation's operating-model default. When an override is off, the location follows
        the operating-model default.
      </div>

      <Card>
        <CardContent className="pt-6 space-y-6">
          {/* Persistent location */}
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label className="text-sm font-medium">
                Treat this location as persistent
              </Label>
              <p className="text-xs text-muted-foreground">
                Persistent locations have gear installed long-term (e.g. facilities).
                {!isPersistenceOverridden && (
                  <span className="ml-1 text-amber-700">
                    Default: {effective.default_location_persistence}
                  </span>
                )}
              </p>
              {isPersistenceOverridden && (
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                  Overridden
                </Badge>
              )}
            </div>
            <Switch
              checked={isPersistenceOverridden}
              onCheckedChange={handleTogglePersistence}
              disabled={updateLocation.isPending}
            />
          </div>

          <div className="border-t" />

          {/* Track installed gear */}
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label className="text-sm font-medium">
                Track installed gear here
              </Label>
              <p className="text-xs text-muted-foreground">
                Enables tracking of gear installed in-place at this location.
                {!isTrackGearOverridden && (
                  <span className="ml-1 text-amber-700">
                    Default: {effective.field_relevance.track_installed_gear ? 'on' : 'off'}
                  </span>
                )}
              </p>
              {isTrackGearOverridden && (
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                  Overridden
                </Badge>
              )}
            </div>
            <Switch
              checked={isTrackGearOverridden}
              onCheckedChange={handleToggleTrackGear}
              disabled={updateLocation.isPending}
            />
          </div>

          <div className="border-t" />

          {/* Gear lifecycle */}
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label className="text-sm font-medium">
                Inspect gear in-place
              </Label>
              <p className="text-xs text-muted-foreground">
                Gear is inspected where it lives instead of on return.
                {!isGearLifecycleOverridden && (
                  <span className="ml-1 text-amber-700">
                    Default: {effective.gear_lifecycle_default === 'inspect_in_place' ? 'on' : 'off'}
                  </span>
                )}
              </p>
              {isGearLifecycleOverridden && (
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                  Overridden
                </Badge>
              )}
            </div>
            <Switch
              checked={isGearLifecycleOverridden}
              onCheckedChange={handleToggleGearLifecycle}
              disabled={updateLocation.isPending}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Delete Buttons ──────────────────────────────────────────────────────────

function DeleteZoneButton({
  zone,
  label,
  descendantCount = 0,
}: {
  zone: Zone;
  label: string;
  descendantCount?: number;
}) {
  const deleteMutation = useDeleteZone();

  const handleDelete = async () => {
    const descendantWarning =
      descendantCount > 0
        ? ` This will also remove ${descendantCount} nested ${label.toLowerCase()}(s).`
        : '';
    if (!confirm(`Remove ${label.toLowerCase()} "${zone.name}"?${descendantWarning}`)) return;
    try {
      await deleteMutation.mutateAsync(zone.id);
      toast.success(`${zone.name} removed`);
    } catch (err) {
      const apiError = handleApiError(err);
      toast.error(apiError.message);
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 text-red-600 hover:text-red-700"
      onClick={handleDelete}
      disabled={deleteMutation.isPending}
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  );
}

function DeleteSpotButton({ spot, label }: { spot: Spot; label: string }) {
  const deleteMutation = useDeleteSpot();

  const handleDelete = async () => {
    if (!confirm(`Remove ${label.toLowerCase()} "${spot.name}"?`)) return;
    try {
      await deleteMutation.mutateAsync(spot.id);
      toast.success(`${spot.name} removed`);
    } catch (err) {
      const apiError = handleApiError(err);
      toast.error(apiError.message);
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 text-red-600 hover:text-red-700"
      onClick={handleDelete}
      disabled={deleteMutation.isPending}
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  );
}

// ─── Zone Tree ───────────────────────────────────────────────────────────────

function buildZoneChildrenMap(zones: Zone[]) {
  const map = new Map<number | null, Zone[]>();
  for (const zone of zones) {
    const parent = zone.parent_zone ?? null;
    if (!map.has(parent)) {
      map.set(parent, []);
    }
    map.get(parent)!.push(zone);
  }
  for (const list of map.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }
  return map;
}

function getZoneChildren(
  map: Map<number | null, Zone[]>,
  parentId: number | null
): Zone[] {
  return map.get(parentId) ?? [];
}

function countDescendants(map: Map<number | null, Zone[]>, parentId: number): number {
  const children = getZoneChildren(map, parentId);
  return children.reduce((sum, child) => sum + 1 + countDescendants(map, child.id), 0);
}

interface ZoneTreeRowProps {
  zone: Zone;
  depth: number;
  childrenMap: Map<number | null, Zone[]>;
  labels: { zone: string };
  onEdit: (zone: Zone) => void;
  onAddChild: (zone: Zone) => void;
}

function ZoneTreeRow({
  zone,
  depth,
  childrenMap,
  labels,
  onEdit,
  onAddChild,
}: ZoneTreeRowProps) {
  const [expanded, setExpanded] = useState(true);
  const children = getZoneChildren(childrenMap, zone.id);
  const hasChildren = children.length > 0;
  const descendantCount = countDescendants(childrenMap, zone.id);
  const typeLabel =
    ZONE_TYPES.find((t) => t.value === zone.type)?.label ?? zone.type;

  return (
    <>
      <TableRow>
        <TableCell className="font-medium">
          <div
            className="flex items-center gap-2"
            style={{ paddingLeft: `${depth * 1.5}rem` }}
          >
            {hasChildren ? (
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="rounded p-0.5 hover:bg-gray-100"
                aria-label={expanded ? 'Collapse' : 'Expand'}
              >
                <ChevronRight
                  className={cn(
                    'h-4 w-4 text-gray-500 transition-transform',
                    expanded && 'rotate-90'
                  )}
                />
              </button>
            ) : (
              <span className="w-6" />
            )}
            <span>{zone.name}</span>
          </div>
        </TableCell>
        <TableCell>{typeLabel}</TableCell>
        <TableCell>
          <Badge
            variant="outline"
            className={
              zone.is_active
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            }
          >
            {zone.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </TableCell>
        <TableCell>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onAddChild(zone)}
              title={`Add child ${labels.zone.toLowerCase()}`}
              aria-label={`Add child ${labels.zone.toLowerCase()}`}
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onEdit(zone)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <DeleteZoneButton
              zone={zone}
              label={labels.zone}
              descendantCount={descendantCount}
            />
          </div>
        </TableCell>
      </TableRow>
      {expanded &&
        children.map((child) => (
          <ZoneTreeRow
            key={child.id}
            zone={child}
            depth={depth + 1}
            childrenMap={childrenMap}
            labels={labels}
            onEdit={onEdit}
            onAddChild={onAddChild}
          />
        ))}
    </>
  );
}

interface ZoneTreeTableProps {
  zones: Zone[];
  labels: { zone: string };
  onEdit: (zone: Zone) => void;
  onAddChild: (zone: Zone) => void;
}

function ZoneTreeTable({ zones, labels, onEdit, onAddChild }: ZoneTreeTableProps) {
  const childrenMap = useMemo(() => buildZoneChildrenMap(zones), [zones]);
  const roots = getZoneChildren(childrenMap, null);

  return (
    <div className="hidden lg:block border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Active</TableHead>
            <TableHead className="w-32">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {roots.map((zone) => (
            <ZoneTreeRow
              key={zone.id}
              zone={zone}
              depth={0}
              childrenMap={childrenMap}
              labels={labels}
              onEdit={onEdit}
              onAddChild={onAddChild}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

interface ZoneTreeCardProps {
  zone: Zone;
  depth: number;
  childrenMap: Map<number | null, Zone[]>;
  labels: { zone: string };
  onEdit: (zone: Zone) => void;
  onAddChild: (zone: Zone) => void;
}

function ZoneTreeCard({
  zone,
  depth,
  childrenMap,
  labels,
  onEdit,
  onAddChild,
}: ZoneTreeCardProps) {
  const [expanded, setExpanded] = useState(true);
  const children = getZoneChildren(childrenMap, zone.id);
  const hasChildren = children.length > 0;
  const descendantCount = countDescendants(childrenMap, zone.id);
  const typeLabel =
    ZONE_TYPES.find((t) => t.value === zone.type)?.label ?? zone.type;

  return (
    <div style={{ paddingLeft: `${depth * 0.75}rem` }}>
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1">
                {hasChildren ? (
                  <button
                    type="button"
                    onClick={() => setExpanded((e) => !e)}
                    className="rounded p-0.5 hover:bg-gray-100"
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                  >
                    <ChevronRight
                      className={cn(
                        'h-4 w-4 text-gray-500 transition-transform',
                        expanded && 'rotate-90'
                      )}
                    />
                  </button>
                ) : (
                  <span className="w-6" />
                )}
                <p className="font-medium text-gray-900">{zone.name}</p>
              </div>
              <p className="text-xs text-muted-foreground">{typeLabel}</p>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onAddChild(zone)}
                title={`Add child ${labels.zone.toLowerCase()}`}
                aria-label={`Add child ${labels.zone.toLowerCase()}`}
              >
                <Plus className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onEdit(zone)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <DeleteZoneButton
                zone={zone}
                label={labels.zone}
                descendantCount={descendantCount}
              />
              <Badge
                variant="outline"
                className={
                  zone.is_active
                    ? 'bg-green-50 text-green-700 flex-shrink-0'
                    : 'bg-red-50 text-red-700 flex-shrink-0'
                }
              >
                {zone.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
      {expanded && children.length > 0 && (
        <div className="space-y-2 mt-2">
          {children.map((child) => (
            <ZoneTreeCard
              key={child.id}
              zone={child}
              depth={depth + 1}
              childrenMap={childrenMap}
              labels={labels}
              onEdit={onEdit}
              onAddChild={onAddChild}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface ZoneTreeCardsProps {
  zones: Zone[];
  labels: { zone: string };
  onEdit: (zone: Zone) => void;
  onAddChild: (zone: Zone) => void;
}

function ZoneTreeCards({ zones, labels, onEdit, onAddChild }: ZoneTreeCardsProps) {
  const childrenMap = useMemo(() => buildZoneChildrenMap(zones), [zones]);
  const roots = getZoneChildren(childrenMap, null);

  return (
    <div className="lg:hidden space-y-2">
      {roots.map((zone) => (
        <ZoneTreeCard
          key={zone.id}
          zone={zone}
          depth={0}
          childrenMap={childrenMap}
          labels={labels}
          onEdit={onEdit}
          onAddChild={onAddChild}
        />
      ))}
    </div>
  );
}

// ─── Edit Zone Dialog ────────────────────────────────────────────────────────

function EditZoneDialog({
  zone,
  onClose,
  locationId,
  labels,
}: {
  zone: Zone | null;
  onClose: () => void;
  locationId: number;
  labels: { zone: string };
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState<ZoneType>('area');
  const [parentZoneId, setParentZoneId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const updateMutation = useUpdateZone();

  useEffect(() => {
    if (zone) {
      setName(zone.name);
      setType(zone.type);
      setParentZoneId(zone.parent_zone ?? null);
      setError(null);
    }
  }, [zone]);

  const handleSubmit = async () => {
    if (!zone) return;
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: zone.id,
        data: { name: name.trim(), type, parent_zone: parentZoneId },
      });
      toast.success(`${name.trim()} updated`);
      onClose();
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  return (
    <Dialog open={zone !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit {labels.zone}</DialogTitle>
          <DialogDescription>Rename or retype this {labels.zone.toLowerCase()}.</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="edit-zone-name">Name *</Label>
            <Input id="edit-zone-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-zone-type">Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as ZoneType)}>
              <SelectTrigger id="edit-zone-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ZONE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Parent {labels.zone}</Label>
            <ZonePicker
              locationId={locationId}
              value={parentZoneId}
              onChange={setParentZoneId}
              excludeTreeRootId={zone?.id}
              placeholder={`Select a parent ${labels.zone.toLowerCase()} (optional)`}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={updateMutation.isPending}>
            {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit Spot Dialog ────────────────────────────────────────────────────────

function EditSpotDialog({
  spot,
  onClose,
  zones,
  labels,
}: {
  spot: Spot | null;
  onClose: () => void;
  zones: Zone[];
  labels: { zone: string; spot: string };
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState<SpotType>('toolbox');
  const [zoneId, setZoneId] = useState<string>('none');
  const [error, setError] = useState<string | null>(null);
  const updateMutation = useUpdateSpot();

  useEffect(() => {
    if (spot) {
      setName(spot.name);
      setType(spot.type);
      setZoneId(spot.zone ? spot.zone.toString() : 'none');
      setError(null);
    }
  }, [spot]);

  const handleSubmit = async () => {
    if (!spot) return;
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: spot.id,
        data: {
          name: name.trim(),
          type,
          zone: zoneId !== 'none' ? Number(zoneId) : null,
        },
      });
      toast.success(`${name.trim()} updated`);
      onClose();
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  return (
    <Dialog open={spot !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit {labels.spot}</DialogTitle>
          <DialogDescription>Rename or retype this {labels.spot.toLowerCase()}.</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="edit-spot-name">Name *</Label>
            <Input id="edit-spot-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-spot-type">Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as SpotType)}>
              <SelectTrigger id="edit-spot-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SPOT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-spot-zone">{labels.zone} (optional)</Label>
            <Select value={zoneId} onValueChange={setZoneId}>
              <SelectTrigger id="edit-spot-zone">
                <SelectValue placeholder={`Select a ${labels.zone.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (attach directly)</SelectItem>
                {zones.map((z) => (
                  <SelectItem key={z.id} value={z.id.toString()}>
                    {z.path}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={updateMutation.isPending}>
            {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Apply Template Dialog ───────────────────────────────────────────────────

function ApplyTemplateDialog({
  open,
  onOpenChange,
  locationId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: number;
}) {
  const { data: templates } = useLocationTemplates();
  const applyMutation = useApplyTemplate();

  const [selected, setSelected] = useState<LocationTemplate | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setSelected(null);
    setCounts({});
    setError(null);
  };

  const handleClose = (val: boolean) => {
    if (!val) resetForm();
    onOpenChange(val);
  };

  const getParamKey = (tpl: LocationTemplate | null): string | null => {
    if (!tpl) return null;
    const paramMap: Record<string, string> = {
      building: 'floors',
      industrial: 'units',
    };
    return paramMap[tpl.key] ?? null;
  };

  const handleSelect = (tpl: LocationTemplate) => {
    setSelected(tpl);
    // Reset counts to only the current template's param
    const pk = getParamKey(tpl);
    if (pk) {
      setCounts({ [pk]: counts[pk] ?? 3 });
    } else {
      setCounts({});
    }
  };

  const paramKey = getParamKey(selected);

  const handleSubmit = async () => {
    setError(null);
    if (!selected || selected.key === 'blank') {
      setError('Please select a template (not Blank).');
      return;
    }
    try {
      const result = await applyMutation.mutateAsync({
        locationId,
        payload: {
          template_key: selected.key,
          counts: Object.keys(counts).length > 0 ? counts : undefined,
        },
      });
      toast.success(
        `Template applied: ${result.zones_created} zone(s), ${result.spots_created} spot(s) added`
      );
      handleClose(false);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  const nonBlankTemplates = (templates ?? []).filter((t) => t.key !== 'blank');

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Apply a template</DialogTitle>
          <DialogDescription>
            This will <strong>append</strong> zones and spots to the existing location shape.
            Nothing currently on this location will be removed.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3 py-2">
          {nonBlankTemplates.map((tpl) => (
            <Card
              key={tpl.key}
              className={`cursor-pointer transition-colors ${
                selected?.key === tpl.key
                  ? 'border-blue-500 bg-blue-50'
                  : 'hover:border-gray-400'
              }`}
              onClick={() => handleSelect(tpl)}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-sm">{tpl.label}</p>
                  {selected?.key === tpl.key && (
                    <Badge variant="default" className="ml-2">Selected</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {paramKey && (
            <div className="space-y-2 pt-2">
              <Label htmlFor="apply-tpl-count">
                {TEMPLATE_PARAM_LABELS[paramKey] ?? paramKey}
              </Label>
              <Input
                id="apply-tpl-count"
                type="number"
                min={1}
                max={100}
                value={counts[paramKey] ?? 3}
                onChange={(e) =>
                  setCounts((prev) => ({
                    ...prev,
                    [paramKey]: parseInt(e.target.value, 10) || 1,
                  }))
                }
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={applyMutation.isPending}>
            {applyMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Apply template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Add Zone Dialog ─────────────────────────────────────────────────────────

function AddZoneDialog({
  open,
  onOpenChange,
  locationId,
  parentZoneId: parentZoneIdProp,
  industry,
  labels,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: number;
  parentZoneId?: number | null;
  industry: string;
  labels: { zone: string };
}) {
  const defaultType: ZoneType = ZONE_TYPE_DEFAULTS[(industry as Industry)] ?? 'area';
  const [name, setName] = useState('');
  const [type, setType] = useState<ZoneType>(defaultType);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setType(defaultType); }, [defaultType]);
  const [description, setDescription] = useState('');
  const [parentZoneId, setParentZoneId] = useState<number | null>(parentZoneIdProp ?? null);
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateZone();

  useEffect(() => {
    setParentZoneId(parentZoneIdProp ?? null);
  }, [parentZoneIdProp]);

  const resetForm = () => {
    setName('');
    setType(defaultType);
    setDescription('');
    setParentZoneId(parentZoneIdProp ?? null);
    setError(null);
  };

  const handleClose = (val: boolean) => {
    if (!val) resetForm();
    onOpenChange(val);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    try {
      const zone = await createMutation.mutateAsync({
        location: locationId,
        parent_zone: parentZoneId,
        name: name.trim(),
        type,
        description: description.trim() || undefined,
      });
      toast.success(`${zone.name} created`);
      handleClose(false);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add {labels.zone}</DialogTitle>
          <DialogDescription>Create a new {labels.zone.toLowerCase()}.</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="zone-name">Name *</Label>
            <Input id="zone-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="zone-type">Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as ZoneType)}>
              <SelectTrigger id="zone-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ZONE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="zone-desc">Description</Label>
            <Input id="zone-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Parent {labels.zone}</Label>
            <ZonePicker
              locationId={locationId}
              value={parentZoneId}
              onChange={setParentZoneId}
              placeholder={`Select a parent ${labels.zone.toLowerCase()} (optional)`}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add {labels.zone}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Add Spot Dialog ─────────────────────────────────────────────────────────

function AddSpotDialog({
  open,
  onOpenChange,
  locationId,
  zones,
  industry,
  labels,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: number;
  zones: Zone[];
  industry: string;
  labels: { zone: string; spot: string };
}) {
  const defaultSpotType: SpotType = SPOT_TYPE_DEFAULTS[(industry as Industry)] ?? 'toolbox';
  const [name, setName] = useState('');
  const [type, setType] = useState<SpotType>(defaultSpotType);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setType(defaultSpotType); }, [defaultSpotType]);
  const [zoneId, setZoneId] = useState<string>('none');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateSpot();

  const resetForm = () => {
    setName('');
    setType(defaultSpotType);
    setZoneId('none');
    setError(null);
  };

  const handleClose = (val: boolean) => {
    if (!val) resetForm();
    onOpenChange(val);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    try {
      const spot = await createMutation.mutateAsync({
        location: locationId,
        zone: zoneId !== 'none' ? Number(zoneId) : undefined,
        name: name.trim(),
        type,
      });
      toast.success(`${spot.name} created`);
      handleClose(false);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add {labels.spot}</DialogTitle>
          <DialogDescription>Create a new {labels.spot.toLowerCase()}.</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="spot-name">Name *</Label>
            <Input id="spot-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="spot-type">Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as SpotType)}>
              <SelectTrigger id="spot-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SPOT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="spot-zone">{labels.zone} (optional)</Label>
            <Select value={zoneId} onValueChange={setZoneId}>
              <SelectTrigger id="spot-zone">
                <SelectValue placeholder={`Select a ${labels.zone.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (attach directly)</SelectItem>
                {zones.map((z) => (
                  <SelectItem key={z.id} value={z.id.toString()}>
                    {z.path}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add {labels.spot}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
